export interface WeekEntry {
  kind: "week";
  number: 1 | 2 | 3 | 4 | 5 | 6;
  window: string;
  title: string;
  focus: string;
  gate: string;
  deliverables: string[];
  conclusions: string[] | null;
}

export type CheckpointTone = "transition" | "build" | "select";

export interface CheckpointEntry {
  kind: "checkpoint";
  id: string;
  tone: CheckpointTone;
  date: string;
  title: string;
  outcome: string;
  bullets: string[];
}

export type TimelineEntry = WeekEntry | CheckpointEntry;

const WEEK_RANGES: Array<{ number: 1 | 2 | 3 | 4 | 5 | 6; start: string; end: string }> = [
  { number: 1, start: "2026-05-04", end: "2026-05-10" },
  { number: 2, start: "2026-05-11", end: "2026-05-17" },
  { number: 3, start: "2026-05-18", end: "2026-05-24" },
  { number: 4, start: "2026-05-25", end: "2026-05-31" },
  { number: 5, start: "2026-06-01", end: "2026-06-07" },
  { number: 6, start: "2026-06-08", end: "2026-06-14" },
];

export function getCurrentWeekNumber(now: Date = new Date()): 1 | 2 | 3 | 4 | 5 | 6 | null {
  const today = now.toISOString().slice(0, 10);
  for (const { number, start, end } of WEEK_RANGES) {
    if (today >= start && today <= end) return number;
  }
  return null;
}

export const timelineV3: TimelineEntry[] = [
  {
    kind: "week",
    number: 1,
    window: "May 4 – May 10",
    title: "Mapping current systems — part 1",
    focus:
      "Discover and document how Yandex's PIM, supply chain and B2B architecture work today, and where they break for the Israeli B2B model.",
    gate: "Current-state architecture understood end-to-end for supply chain + PIM.",
    deliverables: [
      "High-level architecture diagram",
      "System integration map",
      "Dependency tracker with owners",
    ],
    conclusions: [
      "Hybrid B2B architecture locked — new independent B2B stack with its own PIM, Woody (ERP) kept for supplier ordering.",
      "Yandex PIM disqualified for B2B — cannot manage inventory by batch / expiration dates (supermarkets require 30-day minimum shelf life).",
      "Six PIM gaps surfaced: no GS1 integration, no cluster modelling, duplicated per-language fields, weak search, no staging environment, overly-granular permissions.",
      "Replace-all-Yandex assumption restated by Maor — creates tension with the May 5 hybrid decision; flagged as open.",
    ],
  },
  {
    kind: "week",
    number: 2,
    window: "May 11 – May 17",
    title: "Mapping current systems — part 2",
    focus:
      "Map the operational, analytics, pricing, content and warehouse layers — find the load-bearing blockers.",
    gate: "All major system domains mapped; load-bearing blockers named.",
    deliverables: [
      "Domain map (BI, Pricing, CMS, WMS, Delivery)",
      "Gap analysis per domain",
      "Blocker list with severity",
    ],
    conclusions: [
      "CRM absence is the top blocker — no Yandex Passport in Israel; without a customer-identity layer, BI / segmentation / loyalty are all structurally constrained.",
      "Yango's 0–2% missing-items rate (scan-to-shelf + cycle counts) is the competitive moat any replacement WMS must preserve.",
      "~200 sqm stores cannot absorb B2B order sizes — options: dedicated dark-store warehouse vs Netanya-style 500–600 sqm store with split inventory (open).",
      "Pricing: upstream manual workflow loses discount-breakdown detail; mixed-price bundles unsupported; coupon admin is raw JSON.",
      "Content: Admin Panel / Adminca split is a pain point; banner/button/story desync; content team dev-blocked on Russia side (Leoni).",
    ],
  },
  {
    kind: "checkpoint",
    id: "owner-checkpoint",
    tone: "transition",
    date: "May 13",
    title: "Owner checkpoint — locked direction",
    outcome: "Build B2B from scratch — no Yandex component reuse.",
    bullets: [
      "Two roadmaps presented to owner: reuse Yandex vs build from scratch.",
      "Reuse-Yandex fork dropped; from-scratch path adopted for the B2B.",
      "Downstream effect — Week 3 pivots from current-state mapping to B2B MVP spec work.",
    ],
  },
  {
    kind: "week",
    number: 3,
    window: "May 18 – May 24",
    title: "B2B MVP refinement",
    focus:
      "Translate the from-scratch decision into a concrete B2B MVP — what we ship, how money flows, how customers come on board.",
    gate: "MVP scope and B2B onboarding flow agreed by owner + product.",
    deliverables: [
      "New ecosystem MVP feature list",
      "Payment flow specification",
      "Invoicing integration spec",
      "Onboarding B2B users spec",
    ],
    conclusions: null,
  },
  {
    kind: "week",
    number: 4,
    window: "May 25 – May 31",
    title: "Market research lands · ERPNext revealed as open-source candidate",
    focus:
      "Convert the ~16-product market research into named architecture candidates and validate each with the operators who would run it. The week is dominated by deep-dive walkthroughs — S1 (current state) and ERPNext (open-source contender) — to surface real fit-or-fail evidence before the extension's ranking work.",
    gate: "Three competing architectures named (Priority · Comax · open-source/ERPNext) and at least one open-source candidate (ERPNext) walked through end-to-end with Yango operators on the supply-chain, dispatch, and pricing axes.",
    deliverables: [
      "S1 WMS walkthrough (May 26) — Day-1 replacement target characterised; hybrid Woody-plus-new-WMS architecture sketched with Leon",
      "ERPNext walkthrough — supply-chain / Buying-Stock-Selling modules (May 27, Meital feedback session)",
      "ERPNext walkthrough — dark-store / dispatch operations (May 27, Omri + Imri session)",
      "ERPNext walkthrough — pricing + procurement (May 27, Boaz session)",
      "Three named candidate architectures established for the Phase-03 ranking work",
    ],
    conclusions: [
      "Market research over the prior weeks scored ~16 products against the rubric — three architectures emerge as the live competition: Priority-anchored, Comax-anchored, and open-source/ERPNext-anchored.",
      "ERPNext (MIT-licensed) surfaced as the open-source contender — feedback sessions ran across three stakeholder axes in one day (May 27): supply-chain operator (Meital), dispatch (Omri + Imri), pricing/procurement (Boaz).",
      "Auto-order load-balancing engine is the non-negotiable for any ERP candidate — Meital flagged this is '20-people work done by 4 people' and the entire central-warehouse economics depend on it; replacement must preserve it.",
      "S1 has severe Day-1-replacement-grade limitations (VPN-only Russian UI, no warehouse locations, no returns/damages/expiry reporting, unresponsive support vendor) — Day-1 replacement direction strengthened.",
      "Phased migration shape agreed informally: replace S1 first for B2C, onboard a small non-critical B2B supplier in parallel, scale gradually with Woody kept as B2B front-end and fallback.",
      "Original 'development proposal' deliverable deferred into Phase 03 — the candidate ranking work needs to land first.",
    ],
  },
  {
    kind: "checkpoint",
    id: "extension-kickoff",
    tone: "build",
    date: "Jun 1",
    title: "Extension kickoff — market-research lens",
    outcome:
      "Extend the runway by two weeks to evaluate concrete architecture candidates against the rubric before committing.",
    bullets: [
      "Recognised that the from-scratch decision still leaves the build-vs-buy question open per component slot.",
      "Two-week extension agreed: market-research the candidate landscape, then score each against the top-weighted criteria.",
      "Output: a ranked candidate set Yango can choose from with eyes-open trade-offs.",
    ],
  },
  {
    kind: "week",
    number: 5,
    window: "Jun 1 – Jun 7",
    title: "Market research, composition & ranking",
    focus:
      "Survey vendor + open-source options across every architecture component slot. Score each product against the 16-criterion rubric, seed feature_products coverage, then compose and rank full-stack candidates under the top-weighted criteria so Yango sees the actual trade-offs.",
    gate: "Three or more named architecture candidates ranked end-to-end — every product evaluated (4,737 feature_products rows, 189 features × 26 products) and every candidate carries a composite score, native-coverage %, and a 'why yes / why pause' trade-off card.",
    deliverables: [
      "Full product-evaluation framework seeded (26 products × 16 criteria)",
      "Strict-audited feature_products coverage matrix (native / plugin / custom / irrelevant)",
      "Composed candidates (e.g. fully-open-source, fully-shelf-products-comax, fully-shelf-products-priority)",
      "Live candidate ranking under the active weights preset, with per-candidate trade-off card",
      "Recommendation memo with the single-candidate proposal for owner sign-off",
    ],
    conclusions: null,
  },
  {
    kind: "week",
    number: 6,
    window: "Jun 8 – Jun 14",
    title: "Candidate selection & development proposal",
    focus:
      "Lock the candidate Yango will commit to and build the Development Proposal off it — timeline, cost, staffing, risk register, post-MVP roadmap.",
    gate: "Selected architecture locked; Development Proposal with timeline + cost approved by owner; ready to hand off to the build team.",
    deliverables: [
      "Selected architecture candidate (one of the ranked set from Week 5)",
      "Development Proposal — phased timeline with milestones, resource plan, cost model",
      "Per-component build-vs-buy commitments derived from the selected candidate",
      "Risk register and post-MVP roadmap aligned to the chosen stack",
      "Owner sign-off on the proposal",
    ],
    conclusions: null,
  },
  {
    kind: "checkpoint",
    id: "candidate-selection",
    tone: "select",
    date: "Jun 14",
    title: "Architecture locked & proposal accepted",
    outcome:
      "Yango commits to the architecture; the Development Proposal (timeline + cost) is signed; build planning begins.",
    bullets: [
      "Ranked candidate set walked through with Tal — composite scores, native-coverage %, and trade-off cards.",
      "Single candidate locked under the active weights preset; per-component product picks final.",
      "Development Proposal (timeline + cost + staffing) approved and ready for build team handoff.",
      "Open follow-ups: weight-tuning sessions, deferred dealbreaker reviews, post-MVP roadmap milestones.",
    ],
  },
];
